import styled from 'styled-components';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { GhostButton } from 'ui/button';
import { Tooltip } from 'ui/tooltip';
import { FormField } from 'ui/formField';
import { arrayMoveImmutable } from 'utils/arrayMove';
import { colors } from 'ui/theme';
import { Add16, NegativeFill } from 'ui/icons';
import { ButtonTitle } from 'modules/templateEditor/common/styles';
import { useCallback } from 'react';
import { InAppContentDefinition, RokuButtonContent } from 'modules/graphql/types';
import { useEditableFieldsContext } from 'modules/templateEditor/context/EditableFieldsContext';
import { useQueryParamContext } from 'modules/templateEditor/context/QueryParamContext';
import { useErrorValidationContext } from 'modules/templateEditor/context/ErrorValidationContext';
import { constructErrorInlineText } from 'modules/templateEditor/common/ErrorInlineText';
import {
  constructFieldErrors,
  FieldNames,
  SettingsReadOnlyText,
  getIsEditMode,
} from 'modules/templateEditor/common';
import { SETTINGS_DISPLAY_CONTENT } from 'modules/templateEditor/EditInApp/constants';
import { ButtonTypeVariations } from './ButtonTypeVariations';

const StyledReadOnlyText = styled(SettingsReadOnlyText)`
  overflow-wrap: anywhere;
`;

const StyledAddIcon = styled(Add16)`
  margin-right: 15px;
`;

const defaultActionButton: RokuButtonContent = {
  buttonText: 'Button',
  buttonTextColorFocused: '0x000000',
  buttonColorFocused: '0xFFFFFF',
  buttonTextColorUnFocused: '0x000000',
  buttonDeeplink: '',
  buttonColorUnFocused: '0xCCCCCC',
  buttonBorderColor: '0xCCCCCC',
};

export const RokuActionButtons = () => {
  const { view } = useQueryParamContext();
  const {
    isEditMode: isEditModeContext,
    liveTemplate,
    setCurrentMediumField,
  } = useEditableFieldsContext();
  const { localeContentErrors, localeContentWarnings } = useErrorValidationContext();

  const { ottPayload } = (liveTemplate?.mediumData || {}) as InAppContentDefinition;
  const rokuButtons = (ottPayload?.buttons || []) as RokuButtonContent[];

  const isEditMode = getIsEditMode(view, isEditModeContext);

  const setActionButtonList = (newButtons: RokuButtonContent[]) =>
    setCurrentMediumField('ottPayload', { ...ottPayload, buttons: newButtons });

  const handleAddParam = () => {
    setActionButtonList([...rokuButtons, defaultActionButton]);
  };

  const sortActionButtonList = useCallback(
    (dragEndObj: any) => {
      const sourceIndex = dragEndObj.source.index;
      const destinationIndex = dragEndObj.destination?.index;

      if (typeof sourceIndex === 'number' && typeof destinationIndex === 'number') {
        const newArrayList = arrayMoveImmutable(rokuButtons, sourceIndex, destinationIndex);
        setActionButtonList(newArrayList);
      }
    },
    [rokuButtons],
  );

  const getButtonTitles = (index: number, title: string) => {
    return index + 1 !== rokuButtons.length ? `${title},` : title;
  };

  const { hasError: hasActionButtonError } = constructFieldErrors({
    contentErrors: localeContentErrors,
    contentWarnings: localeContentWarnings,
    fieldName: FieldNames.ROKU_ACTION_BUTTONS,
  });

  const actionButtonsErrorText = constructErrorInlineText({
    condition: isEditMode,
    hasError: hasActionButtonError,
  });

  return (
    <FormField
      isHorizontalLayout
      labelText={SETTINGS_DISPLAY_CONTENT.roku.actionButtons.label}
      hasError={hasActionButtonError}
      errorText={actionButtonsErrorText}
      data-test="form-field-roku-buttons"
    >
      {isEditMode ? (
        <>
          <DragDropContext onDragEnd={sortActionButtonList}>
            <Droppable droppableId="action-buttons-droppable">
              {(droppableProvided) => (
                <div {...droppableProvided.droppableProps} ref={droppableProvided.innerRef}>
                  {rokuButtons.map((value: RokuButtonContent, index: number) => (
                    <Draggable key={`item-${index}`} draggableId={`item-${index}`} index={index}>
                      {(draggableProvided) => (
                        <div
                          ref={draggableProvided.innerRef}
                          {...draggableProvided.draggableProps}
                          {...draggableProvided.dragHandleProps}
                        >
                          <ButtonTypeVariations index={index} button={value} />
                        </div>
                      )}
                    </Draggable>
                  ))}
                  {droppableProvided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
          <Tooltip
            tooltipContent="Max of two action buttons"
            isEnabled={!!(rokuButtons.length === 2)}
          >
            <GhostButton
              onClick={handleAddParam}
              isDisabled={
                ottPayload?.buttons?.length ===
                SETTINGS_DISPLAY_CONTENT.roku.actionButtons.MAX_NUMBER_OF_BUTTONS
              }
            >
              <StyledAddIcon />
              Add button
            </GhostButton>
          </Tooltip>
        </>
      ) : (
        <StyledReadOnlyText
          isUnset={false}
          $hasError={hasActionButtonError}
          data-test="roku-action-button-ro"
        >
          {ottPayload?.buttons?.length && ottPayload.buttons.length > 0 ? (
            <>
              {hasActionButtonError && <NegativeFill color={colors.red100} />}
              {ottPayload.buttons.map((button: RokuButtonContent, i) => (
                <ButtonTitle key={i} $hasError={hasActionButtonError}>
                  {getButtonTitles(i, button.buttonText)}
                </ButtonTitle>
              ))}
            </>
          ) : (
            SETTINGS_DISPLAY_CONTENT.roku.actionButtons.unset
          )}
        </StyledReadOnlyText>
      )}
    </FormField>
  );
};
